import React, { useState, useCallback, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import StaffSectionLayout from '../../../components/staff/StaffSectionLayout';
import ContentBox from '../../../components/ContentBox';
import {
    FpButton, FpModal, LockBanner, StatusPill, RarityPill, FigureFrame,
    Field, TextInput, NumberInput, Select, Checkbox, RadioRow,
} from '../../../components/frankopop/frankopopShared';
import SetConfigForm from './SetConfigForm';
import {
    useFrankopopData, fetchAdminSet, fetchRarities, fetchSetQuickSellConfig, fetchEffectiveOdds,
    adminUpdateSet, adminUpsertFigure, adminDeleteFigure, adminSetQuickSellValues,
} from '../../../data/frankopop';

const BLANK = { name: '', source: 'figureString', figureString: '', image: '', caseArt: '', rarityId: '', ltd: false, ltdTotal: 5 };

function FigureForm({ initial, rarities, onSave, onCancel }) {
    const [f, setF] = useState(() => ({
        ...BLANK,
        ...initial,
        source: initial?.image ? 'image' : 'figureString',
        rarityId: initial?.rarityId || rarities[0]?.id || '',
    }));
    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

    const save = () => {
        onSave({
            id: initial?.id,
            name: f.name,
            figureString: f.source === 'figureString' ? f.figureString : '',
            image: f.source === 'image' ? f.image : '',
            caseArt: f.caseArt || null,
            rarityId: f.rarityId,
            ltd: f.ltd,
            ltdTotal: f.ltdTotal,
        });
    };

    return (
        <div className="space-y-4">
            <Field label="FrankoPop name" required>
                <TextInput value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Snowman Frank" />
            </Field>

            <Field label="Primary source" hint="A FigureString drives the avatar (clothing, hats, features). Use an image only for FrankoPops that can't be built from assets. One or the other, not both.">
                <RadioRow
                    value={f.source}
                    onChange={(v) => set({ source: v })}
                    options={[{ value: 'figureString', label: 'FigureString' }, { value: 'image', label: 'Image' }]}
                />
            </Field>

            {f.source === 'figureString' ? (
                <Field label="FigureString" required>
                    <TextInput value={f.figureString} onChange={(e) => set({ figureString: e.target.value })} placeholder="hd-180-1.ch-255-66.lg-280-110" />
                </Field>
            ) : (
                <Field label="Image URL" required>
                    <TextInput value={f.image} onChange={(e) => set({ image: e.target.value })} placeholder="/img/… or https://…" />
                </Field>
            )}

            <Field label="Case artwork (optional)" hint="The packaging design for this specific FrankoPop.">
                <TextInput value={f.caseArt} onChange={(e) => set({ caseArt: e.target.value })} placeholder="/img/… or https://…" />
            </Field>

            <Field label="Rarity" required hint="Chance is global, set on the Rarities page - not per FrankoPop or per set.">
                <Select value={f.rarityId} onChange={(e) => set({ rarityId: e.target.value })}>
                    {rarities.map((r) => <option key={r.id} value={r.id}>{r.name}{r.isBaseRarity ? ' (base)' : ` - ${r.chancePercent}%`}</option>)}
                </Select>
            </Field>

            <div className="border-2 border-gray-200 p-3">
                <Checkbox label="Limited (LTD) - cap how many can ever exist" checked={f.ltd} onChange={(v) => set({ ltd: v })} />
                {f.ltd && (
                    <div className="mt-2">
                        <NumberInput min={1} value={f.ltdTotal} onChange={(e) => set({ ltdTotal: e.target.value })} />
                        {initial?.committed > 0 && (
                            <p className="mt-1 text-[11px] text-amber-600">{initial.committed} already committed - can&apos;t go below that.</p>
                        )}
                        <p className="mt-1 text-[11px] text-gray-400">
                            Independent of the set&apos;s box limit - even if every box sells, only this many of this FrankoPop can ever be awarded.
                            LTD FrankoPops are a bonus and don&apos;t count toward set completion.
                        </p>
                    </div>
                )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-1">
                <FpButton variant="secondary" onClick={onCancel}>Cancel</FpButton>
                <FpButton variant="primary" onClick={save} disabled={!f.name.trim()}>{initial?.id ? 'Save FrankoPop' : 'Add FrankoPop'}</FpButton>
            </div>
        </div>
    );
}

function FigureRow({ figure, locked, onEdit, onDelete }) {
    return (
        <div className="flex items-center gap-3 border border-gray-200 bg-white p-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
            <FigureFrame figure={figure} size={64} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-['Jersey_10'] uppercase text-[15px] tracking-[1px] text-gray-900">#{String(figure.number).padStart(2, '0')} {figure.name}</span>
                    <RarityPill name={figure.rarityName} colour={figure.rarityColour} />
                </div>
                <p className="text-[11px] text-gray-400 font-['Jersey_10'] uppercase tracking-[1px] mt-0.5">
                    {figure.source === 'image' ? 'Image' : 'FigureString'}
                    {figure.ltd && ` · LTD ${figure.ltdTotal} (${figure.awarded} awarded, ${figure.reserved} reserved)`}
                </p>
            </div>
            {!locked && (
                <div className="flex gap-2 shrink-0">
                    <FpButton size="sm" variant="ghost" onClick={onEdit}>Edit</FpButton>
                    <FpButton size="sm" variant="danger" disabled={figure.committed > 0} onClick={onDelete}>Delete</FpButton>
                </div>
            )}
        </div>
    );
}

// Read-only - chance itself is global, set on the Rarities page. This just shows
// what that means for THIS set right now: every non-base rarity present uses its
// global %, and the base rarity (if present) shows the remainder.
function EffectiveOddsPreview({ setId }) {
    const { data: rarities, loading } = useFrankopopData(useCallback(() => fetchEffectiveOdds(setId), [setId]), []);
    const list = rarities || [];

    if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
    if (list.length === 0) return <p className="text-sm text-gray-500">Add FrankoPops with a rarity first.</p>;

    return (
        <div className="space-y-2">
            {list.map((r) => (
                <div key={r.rarityId} className="flex items-center gap-3 border border-gray-200 bg-white px-3 py-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
                    <RarityPill name={r.rarityName} colour={r.colour} />
                    <span className="ml-auto font-['Jersey_10'] text-[16px] text-maroon">{r.effectiveChance}%</span>
                </div>
            ))}
        </div>
    );
}

// Quick-sell value only - one per rarity actually used in this set, every figure
// of that rarity shares it. This is still per-set (chance itself is not).
function QuickSellValuesEditor({ setId, locked }) {
    const { data: rarities, loading, refetch } = useFrankopopData(useCallback(() => fetchSetQuickSellConfig(setId), [setId]), []);
    const [edits, setEdits] = useState({});

    useEffect(() => {
        if (!rarities) return;
        const initial = {};
        for (const r of rarities) initial[r.rarityId] = r.quickSellValue;
        setEdits(initial);
    }, [rarities]);

    const list = rarities || [];

    const save = async () => {
        const payload = list.map((r) => ({
            rarityId: r.rarityId,
            quickSellValue: edits[r.rarityId] ?? r.quickSellValue,
        }));
        const res = await adminSetQuickSellValues(setId, payload);
        if (!res.success) { toast.error(res.message); return; }
        toast.success('Quick-sell values saved.');
        refetch();
    };

    if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
    if (list.length === 0) return <p className="text-sm text-gray-500">Add FrankoPops with a rarity first.</p>;

    return (
        <div>
            {locked && (
                <LockBanner>
                    Quick-sell values are locked once this set is released - players may already own copies priced
                    against these numbers.
                </LockBanner>
            )}

            <div className="space-y-2">
                {list.map((r) => (
                    <div key={r.rarityId} className="flex items-center gap-3 border border-gray-200 bg-white px-3 py-2" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}>
                        <div className="w-40 shrink-0"><RarityPill name={r.rarityName} colour={r.colour} /></div>
                        <div className="flex-1 max-w-[160px]">
                            <NumberInput
                                min={0} step="1" disabled={locked}
                                value={edits[r.rarityId] ?? r.quickSellValue}
                                onChange={(e) => setEdits((prev) => ({ ...prev, [r.rarityId]: e.target.value }))}
                            />
                        </div>
                        <span className="text-xs text-gray-400">framps</span>
                    </div>
                ))}
            </div>

            {!locked && (
                <div className="flex justify-end mt-3">
                    <FpButton variant="primary" onClick={save}>Save quick-sell values</FpButton>
                </div>
            )}
        </div>
    );
}

export default function FrankoPopSetEditor() {
    const { setId } = useParams();
    const { data: setData, loading, refetch } = useFrankopopData(useCallback(() => fetchAdminSet(setId), [setId]), null);
    const { data: rarities } = useFrankopopData(useCallback(() => fetchRarities(), []), []);
    const [editingFigure, setEditingFigure] = useState(null); // figure object or 'new'

    if (loading) {
        return (
            <StaffSectionLayout>
                <ContentBox theme="default" tint="red" header="Loading...">
                    <p className="text-sm text-gray-500">Loading set...</p>
                </ContentBox>
            </StaffSectionLayout>
        );
    }

    if (!setData?.set) {
        return (
            <StaffSectionLayout>
                <ContentBox theme="default" tint="red" header="Set not found">
                    <p className="text-sm text-gray-600">That set doesn&apos;t exist. <Link className="underline text-maroon" to="/staff/frankopop/sets">Back to sets</Link></p>
                </ContentBox>
            </StaffSectionLayout>
        );
    }

    const { set, figures } = setData;
    const hasBoxes = set.boxesTotal > 0;

    const saveConfig = async (payload) => {
        const res = await adminUpdateSet(setId, payload);
        if (!res.success) { toast.error(res.message); return; }
        toast.success('Set configuration saved.');
        refetch();
    };

    const saveFigure = async (payload) => {
        const res = await adminUpsertFigure(setId, payload);
        if (!res.success) { toast.error(res.message); return; }
        toast.success(payload.id ? 'FrankoPop updated.' : 'FrankoPop added.');
        setEditingFigure(null);
        refetch();
    };

    const deleteFigure = async (fig) => {
        const res = await adminDeleteFigure(setId, fig.id);
        if (!res.success) { toast.error(res.message); return; }
        toast.success('FrankoPop removed.');
        refetch();
    };

    return (
        <StaffSectionLayout>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Link to="/staff/frankopop/sets" className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-500 hover:text-maroon">← Sets</Link>
                <span className="text-gray-300">/</span>
                <span className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-900">{set.name}</span>
                <StatusPill status={set.status} />
            </div>

            <ContentBox theme="default" tint="red" header="Configuration">
                <SetConfigForm
                    key={set.contentsLockedAt || 'cfg'}
                    initial={set}
                    onSubmit={saveConfig}
                    submitLabel="Save configuration"
                    disableTypeToggle={hasBoxes}
                />
            </ContentBox>

            <ContentBox
                theme="default"
                tint="red"
                header={`Contents - ${figures.length} FrankoPop${figures.length === 1 ? '' : 's'}`}
                headerRight={!set.contentsLocked && <FpButton size="sm" variant="gold" onClick={() => setEditingFigure('new')}>Add FrankoPop</FpButton>}
            >
                {set.contentsLocked && (
                    <LockBanner>
                        This set has {set.giftableOnly ? 'had boxes gifted' : 'been released'}, so its contents are
                        <strong> permanently locked</strong>. FrankoPops, rarities and LTD quantities can no longer be
                        changed - players may already own boxes from it.
                    </LockBanner>
                )}

                {figures.length === 0 ? (
                    <p className="text-sm text-gray-500">No FrankoPops yet. Add the first one.</p>
                ) : (
                    <div className="space-y-2">
                        {figures.map((fig) => (
                            <FigureRow
                                key={fig.id}
                                figure={fig}
                                locked={set.contentsLocked}
                                onEdit={() => setEditingFigure(fig)}
                                onDelete={() => deleteFigure(fig)}
                            />
                        ))}
                    </div>
                )}
            </ContentBox>

            <ContentBox theme="default" tint="red" header="Effective odds in this set">
                <p className="text-sm text-gray-600 mb-3">
                    Chance is global, set per rarity on the{' '}
                    <Link to="/staff/frankopop/rarities" className="underline text-maroon">Rarities page</Link> - this
                    is a read-only preview of what that means for this set right now, given the FrankoPops currently in it.
                </p>
                <EffectiveOddsPreview setId={setId} />
            </ContentBox>

            <ContentBox theme="default" tint="red" header="Quick-sell values">
                <p className="text-sm text-gray-600 mb-3">
                    One quick-sell value per rarity used in this set - every FrankoPop of that rarity shares it. This
                    is still set per set, unlike chance.
                </p>
                <QuickSellValuesEditor setId={setId} locked={set.contentsLocked} />
            </ContentBox>

            {editingFigure && (
                <FpModal
                    title={editingFigure === 'new' ? 'Add a FrankoPop' : `Edit ${editingFigure.name}`}
                    onClose={() => setEditingFigure(null)}
                    maxWidth="max-w-xl"
                >
                    <FigureForm
                        initial={editingFigure === 'new' ? null : editingFigure}
                        rarities={rarities || []}
                        onSave={saveFigure}
                        onCancel={() => setEditingFigure(null)}
                    />
                </FpModal>
            )}
        </StaffSectionLayout>
    );
}
